import DOMPurify from 'dompurify'

// Rich text fields (UiRichTextEditor / UiRichText) store HTML limited to these tags. The server sanitizes
// with the same list when saving (erp-api RichTextSanitizer); this is the second layer, when showing.
export const RICH_TEXT_TAGS = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li', 'a', 'blockquote', 'h3', 'h4', 'code', 'pre', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td']
// data-callout turns a blockquote into a colored notice (Dica, Atenção, Ok, Cuidado); see CALLOUTS.
const RICH_TEXT_ATTRIBUTES = ['href', 'target', 'rel', 'data-callout']
export const CALLOUTS = ['tip', 'warn', 'ok', 'err'] as const
const SAFE_LINK = /^(https?:|mailto:|tel:)/i

// Text saved before the fields were rich: plain, with line breaks. Anything else is treated as HTML.
export function isRichText(value: string | null | undefined): boolean {
  return !!value && /^\s*<(p|ul|ol|h3|h4|blockquote|pre|hr|table)[\s>/]/i.test(value)
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

// Plain text → paragraphs (blank lines) and line breaks, so old values open in the editor as they looked.
export function plainToRichText(text: string | null | undefined): string {
  if (!text?.trim()) return ''
  return text
    .replace(/\r\n?/g, '\n')
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

// The value as the editor and UiRichText use it: rich text as is, old plain text converted.
export function toRichText(value: string | null | undefined): string {
  return isRichText(value) ? (value as string) : plainToRichText(value)
}

let hooked = false
function hookLinks() {
  if (hooked) return
  hooked = true
  // Links only to the web, e-mail or phone, always in a new tab and without access to this page.
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // The notice kind only on a blockquote and only one of the known words.
    if (node.hasAttribute('data-callout') && (node.tagName !== 'BLOCKQUOTE' || !(CALLOUTS as readonly string[]).includes(node.getAttribute('data-callout') ?? ''))) {
      node.removeAttribute('data-callout')
    }
    if (node.tagName !== 'A') return
    const href = node.getAttribute('href') ?? ''
    if (!SAFE_LINK.test(href)) {
      node.removeAttribute('href')
      return
    }
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer nofollow')
  })
}

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return ''
  hookLinks()
  return DOMPurify.sanitize(toRichText(html), {
    ALLOWED_TAGS: RICH_TEXT_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ATTRIBUTES,
    ALLOW_DATA_ATTR: false,
  })
}

// For previews, lists, search and CSV: the text without tags, paragraphs and items on their own lines.
export function richTextToPlain(value: string | null | undefined): string {
  if (!value) return ''
  if (!isRichText(value)) return value
  const doc = new DOMParser().parseFromString(sanitizeRichText(value), 'text/html')
  doc.querySelectorAll('br').forEach((br) => br.replaceWith('\n'))
  doc.querySelectorAll('li').forEach((li) => li.prepend('• '))
  doc.querySelectorAll('td, th').forEach((cell) => cell.append(' '))
  doc.querySelectorAll('p, li, h3, h4, blockquote, pre, tr').forEach((block) => block.append('\n'))
  return (doc.body.textContent ?? '').replace(/ +\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
}

// True when there is no text (the editor leaves "<p></p>" behind when everything is erased).
export function isRichTextEmpty(value: string | null | undefined): boolean {
  return !richTextToPlain(value).trim()
}
